import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  TrendingUp, Calendar, Download, X, FileText, ShoppingCart,
  ChevronLeft, ChevronRight, Loader2, Inbox, Medal, Target,
  ArrowUpRight, Search, Package, Printer
} from "lucide-react";
import styles from "../../styles/Sales.module.css";
import toast, { Toaster } from "react-hot-toast";

axios.defaults.withCredentials = true;

const money = (value) => `₦${Number(value || 0).toLocaleString()}`;

export default function Sales() {
  const [filterDate, setFilterDate] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [salesData, setSalesData] = useState([]);
  const [topSellers, setTopSellers] = useState([]);
  const [statsData, setStatsData] = useState({ revenue: 0, vat: 0, orders: 0, avg: 0 });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchSalesDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const [historyRes, statsRes, topRes] = await Promise.all([
        axios.get(`/api/sales/history?filter=${filterDate}`),
        axios.get(`/api/sales/stats?filter=${filterDate}`),
        axios.get(`/api/sales/top-sellers`)
      ]);
      setSalesData(Array.isArray(historyRes.data) ? historyRes.data : []);
      setStatsData({
        revenue: Number(statsRes.data?.revenue || 0),
        vat: Number(statsRes.data?.vat || 0),
        orders: Number(statsRes.data?.orders || 0),
        avg: Number(statsRes.data?.avg || 0),
      });
      setTopSellers(Array.isArray(topRes.data) ? topRes.data : []);
    } catch (err) {
      console.error("Sales fetch failed", err);
      toast.error("Could not load sales data.");
    } finally {
      setLoading(false);
    }
  }, [filterDate]);

  useEffect(() => { fetchSalesDashboard(); }, [fetchSalesDashboard]);
  useEffect(() => { setCurrentPage(1); }, [filterDate]);

  const handleViewDetails = async (sale) => {
    try {
      const res = await axios.get(`/api/sales/details/${sale.id}`);
      setSelectedSale({ ...sale, items: Array.isArray(res.data) ? res.data : [] });
      setShowDetails(true);
    } catch (err) {
      toast.error("Could not load sale details.");
    }
  };

  const filteredSales = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return salesData;
    return salesData.filter((sale) =>
      sale.id?.toString().includes(q) ||
      sale.payment_method?.toLowerCase().includes(q)
    );
  }, [salesData, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredSales.length / itemsPerPage));
  const currentItems = filteredSales.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const paymentMix = useMemo(() => {
    const result = { cash: 0, pos: 0, transfer: 0 };
    salesData.forEach((sale) => {
      const key = String(sale.payment_method || "").toLowerCase();
      if (Object.prototype.hasOwnProperty.call(result, key)) result[key] += 1;
    });
    return result;
  }, [salesData]);

  const stats = [
    { title: "Sales Revenue", value: money(statsData.revenue), note: "Gross retail sales", icon: <TrendingUp size={21} /> },
    { title: "Total Orders", value: statsData.orders || 0, note: "Completed transactions", icon: <ShoppingCart size={21} /> },
    { title: "Average Sale", value: money(statsData.avg), note: "Average order value", icon: <ArrowUpRight size={21} /> },
    { title: "VAT Collected", value: money(statsData.vat), note: "7.5% VAT recorded", icon: <Target size={21} /> }
  ];

  return (
    <div className={styles.page}>
      <Toaster position="top-right" />

      <section className={styles.hero}>
        <div>
          <span className={styles.eyebrow}>SALES & PERFORMANCE</span>
          <h1>Sales Management</h1>
          <p>Track salon retail sales, payment methods and product performance from one place.</p>
        </div>
        <button className={styles.printReport} onClick={() => window.print()}>
          <Download size={18} /> <span>Print Report</span>
        </button>
      </section>

      <section className={styles.statsGrid}>
        {stats.map((item) => (
          <article className={styles.statCard} key={item.title}>
            <div className={styles.statIcon}>{item.icon}</div>
            <div><span>{item.title}</span><strong>{item.value}</strong><small>{item.note}</small></div>
          </article>
        ))}
      </section>

      <section className={styles.insightGrid}>
        <article className={styles.performanceCard}>
          <div className={styles.sectionHead}>
            <div><span className={styles.kicker}>PAYMENT OVERVIEW</span><h2>How customers are paying</h2></div>
            <span className={styles.orderCount}>{salesData.length} sales</span>
          </div>
          <div className={styles.paymentGrid}>
            <div><span>Cash</span><strong>{paymentMix.cash}</strong></div>
            <div><span>POS</span><strong>{paymentMix.pos}</strong></div>
            <div><span>Transfer</span><strong>{paymentMix.transfer}</strong></div>
          </div>
        </article>

        <article className={styles.topSellers}>
          <div className={styles.sectionHead}>
            <div><span className={styles.kicker}>PRODUCT PERFORMANCE</span><h2>Top Selling Products</h2></div>
            <Medal size={20} />
          </div>
          <div className={styles.topList}>
            {topSellers.length ? topSellers.slice(0, 4).map((item, idx) => (
              <div className={styles.topItem} key={`${item.name}-${idx}`}>
                <span className={styles.rank}>{String(idx + 1).padStart(2, "0")}</span>
                <div><strong>{item.name}</strong><small>{item.total_qty} units sold</small></div>
                <b>{money(item.total_revenue)}</b>
              </div>
            )) : <div className={styles.emptyMini}><Package size={26}/><span>No product sales yet</span></div>}
          </div>
        </article>
      </section>

      <section className={styles.salesPanel}>
        <div className={styles.panelTitleRow}>
          <div><span className={styles.kicker}>TRANSACTIONS</span><h2>Sales History</h2><p>Review completed product sales and reopen receipts.</p></div>
          <div className={styles.toolbar}>
            <label className={styles.searchBox}><Search size={18}/><input value={searchTerm} placeholder="Search invoice or payment..." onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}/></label>
            <label className={styles.filterBox}><Calendar size={17}/><select value={filterDate} onChange={(e) => setFilterDate(e.target.value)}><option value="All">All Time</option><option value="Today">Today</option><option value="Week">Last 7 Days</option><option value="Month">This Month</option></select></label>
          </div>
        </div>

        {loading ? <div className={styles.loading}><Loader2 className={styles.spinner}/><span>Loading sales...</span></div> : currentItems.length ? <>
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead><tr><th>Invoice</th><th>Date</th><th>Payment</th><th>VAT</th><th>Total</th><th></th></tr></thead>
              <tbody>{currentItems.map((sale) => <tr key={sale.id}>
                <td><strong className={styles.invoice}>#DF-{sale.id}</strong></td>
                <td><span className={styles.dateText}>{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString("en-GB", {day:"2-digit", month:"short", year:"numeric"}) : "—"}</span></td>
                <td><span className={`${styles.method} ${styles[String(sale.payment_method || "").toLowerCase()] || ""}`}>{sale.payment_method || "—"}</span></td>
                <td>{money(sale.vat_amount)}</td>
                <td><strong className={styles.amount}>{money(sale.grand_total)}</strong></td>
                <td><button className={styles.viewBtn} onClick={() => handleViewDetails(sale)}><FileText size={17}/><span>View</span></button></td>
              </tr>)}</tbody>
            </table>
          </div>

          <div className={styles.mobileSales}>{currentItems.map((sale) => <article className={styles.saleCard} key={`m-${sale.id}`} onClick={() => handleViewDetails(sale)}>
            <div className={styles.saleCardTop}><div><small>INVOICE</small><strong>#DF-{sale.id}</strong></div><strong className={styles.mobileAmount}>{money(sale.grand_total)}</strong></div>
            <div className={styles.saleCardMeta}><span>{sale.sale_date ? new Date(sale.sale_date).toLocaleDateString("en-GB", {day:"2-digit", month:"short", year:"numeric"}) : "—"}</span><span className={`${styles.method} ${styles[String(sale.payment_method || "").toLowerCase()] || ""}`}>{sale.payment_method || "—"}</span><span>VAT {money(sale.vat_amount)}</span></div>
            <div className={styles.cardView}>View sale <ArrowUpRight size={15}/></div>
          </article>)}</div>
        </> : <div className={styles.empty}><Inbox size={40}/><h3>No sales found</h3><p>Completed product sales will appear here.</p></div>}

        <div className={styles.pagination}>
          <span>Showing {filteredSales.length ? (currentPage - 1) * itemsPerPage + 1 : 0}–{Math.min(currentPage * itemsPerPage, filteredSales.length)} of {filteredSales.length}</span>
          <div><button disabled={currentPage === 1} onClick={() => setCurrentPage((p) => p - 1)}><ChevronLeft size={18}/></button><b>{currentPage} / {totalPages}</b><button disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => p + 1)}><ChevronRight size={18}/></button></div>
        </div>
      </section>

      {showDetails && selectedSale && <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDetails(false); }}>
        <div className={styles.receiptModal}>
          <header className={styles.modalHeader}>
            <div><span className={styles.kicker}>SALE RECEIPT</span><h2>Invoice #DF-{selectedSale.id}</h2><p>{selectedSale.sale_date ? new Date(selectedSale.sale_date).toLocaleString() : "Sale details"}</p></div>
            <button onClick={() => setShowDetails(false)}><X size={20}/></button>
          </header>
          <div className={styles.modalBody}>
            <div className={styles.receiptMeta}><div><span>Payment Method</span><strong>{selectedSale.payment_method || "—"}</strong></div><div><span>Total Paid</span><strong>{money(selectedSale.grand_total)}</strong></div></div>
            <div className={styles.itemsBlock}><span className={styles.kicker}>ITEMS PURCHASED</span>{selectedSale.items?.length ? selectedSale.items.map((item, i) => <div className={styles.itemRow} key={i}><div><strong>{item.name}</strong><span>{item.quantity} × {money(item.unit_price)}</span></div><b>{money(Number(item.unit_price) * Number(item.quantity))}</b></div>) : <p className={styles.noItems}>No item details returned.</p>}</div>
            <div className={styles.bill}><div><span>Subtotal</span><b>{money(Number(selectedSale.grand_total) - Number(selectedSale.vat_amount))}</b></div><div><span>VAT (7.5%)</span><b>{money(selectedSale.vat_amount)}</b></div><div className={styles.totalLine}><span>Total Paid</span><strong>{money(selectedSale.grand_total)}</strong></div></div>
          </div>
          <footer className={styles.modalFooter}><button className={styles.closeAction} onClick={() => setShowDetails(false)}>Close</button><button className={styles.printAction} onClick={() => window.print()}><Printer size={17}/> Print Receipt</button></footer>
        </div>
      </div>}
    </div>
  );
}
